import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  ClipboardList,
  Gauge,
  Wind,
  Cpu,
  Leaf,
  BarChart3,
  FileText,
  ListChecks,
  Wallet,
  CheckCircle2,
} from "lucide-react";
import { getWhatsAppLink } from "@/lib/whatsapp";

const ESSENTIALS = [
  {
    icon: ClipboardList,
    title: "Pre-Audit Data & Baseline",
    points: [
      "12-36 months utility bill analysis (Energy Use Intensity baselines)",
      "Review of drawings, equipment schedules & maintenance logs",
      "Occupancy & equipment run-time schedule mapping",
    ],
  },
  {
    icon: Gauge,
    title: "Condition & Performance Testing",
    points: [
      "Equipment inventory & remaining useful life (chillers, AHUs, VRF, boilers)",
      "Electrical: voltage, power factor, THD, kW demand",
      "Efficiency: chiller kW/TR & COP, heat-exchanger approach temps",
    ],
  },
  {
    icon: Wind,
    title: "Airflow, Thermal & Hydraulics",
    points: [
      "Supply/return CFM, static pressure & duct leakage testing",
      "Chilled/condenser water flow (GPM) & balancing verification",
      "Delta-T across coils, space RH & wet/dry-bulb temperatures",
    ],
  },
  {
    icon: Leaf,
    title: "Indoor Air Quality & Ventilation",
    points: [
      "Fresh-air rates per ASHRAE 62.1 / ISHRAE standards",
      "CO2, PM2.5 / PM10, TVOC and CO spot testing",
      "Filtration MERV/ISO ratings & pressure-drop checks",
    ],
  },
  {
    icon: Cpu,
    title: "Controls, Automation & BMS",
    points: [
      "Sensor calibration (temperature, pressure, airflow, humidity)",
      "Setpoint resets, economizer logic & night setback audit",
      "VFD / modulation verification on fans & pumps",
    ],
  },
  {
    icon: BarChart3,
    title: "Energy Analysis & ECMs",
    points: [
      "Energy balance, thermal losses & simultaneous heat/cool detection",
      "Savings simulation (EnergyPlus / eQUEST)",
      "Financial appraisal: payback (SPP), ROI & NPV",
    ],
  },
];

const DELIVERABLES = [
  {
    icon: FileText,
    title: "Comprehensive Audit Report",
    desc: "Executive summary, baseline energy metrics, equipment-health matrix and detailed thermodynamic & electrical readings.",
  },
  {
    icon: ListChecks,
    title: "Energy Conservation Measure (ECM) Matrix",
    desc: "Prioritised recommendations grouped as No/Low-Cost, Medium-CAPEX (VFDs, re-calibration) and High-CAPEX (chiller / BMS overhaul).",
  },
  {
    icon: Wallet,
    title: "Financial Feasibility & Roadmap",
    desc: "CapEx estimates, annual energy savings (kWh / cost), payback periods and a phased implementation timeline.",
  },
];

const HvacAuditSection = () => {
  return (
    <section
      id="hvac-audit"
      className="py-20 bg-gradient-to-br from-white via-amber-50/40 to-white relative overflow-hidden border-y-2 border-amber-300"
      data-testid="hvac-audit-section"
    >
      <div className="pointer-events-none absolute top-1/4 -right-24 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-14 section-animate">
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-4">Professional Service</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-testid="hvac-audit-title">
            HVAC Cooling, Energy & Indoor Environmental Audit
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            A data-driven audit of your cooling, energy, operational efficiency and indoor air quality -
            uncovering savings and reliability improvements across chillers, AHUs, VRF and ductable systems.
          </p>
        </div>

        {/* Essentials */}
        <h3 className="text-amber-600 font-bold uppercase tracking-wider text-sm mb-6">Core Scope of Services</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {ESSENTIALS.map((item, i) => (
            <Card
              key={item.title}
              className="bg-white border border-gray-200 shadow-md hover:shadow-lg hover:border-amber-300 transition-all duration-300 hover:-translate-y-1"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`hvac-audit-essential-${i}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-br from-amber-500 to-yellow-500 p-2.5 rounded-lg text-black">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <h4 className="font-bold text-gray-900 text-base">{item.title}</h4>
                </div>
                <ul className="space-y-2">
                  {item.points.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      {p}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Deliverables */}
        <h3 className="text-amber-600 font-bold uppercase tracking-wider text-sm mb-6">Detailed Deliverables</h3>
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {DELIVERABLES.map((d, i) => (
            <div
              key={d.title}
              className="rounded-2xl bg-gradient-to-br from-gray-900 to-black p-6 text-white shadow-xl"
              data-testid={`hvac-audit-deliverable-${i}`}
            >
              <div className="inline-flex bg-amber-500/20 text-amber-400 p-2.5 rounded-lg mb-4">
                <d.icon className="h-5 w-5" />
              </div>
              <h4 className="font-bold text-lg mb-2">{d.title}</h4>
              <p className="text-sm text-gray-400 leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <a
            href={getWhatsAppLink(
              "Hi! I'm interested in the HVAC Cooling, Energy & Indoor Environmental Audit service. Please share details, scope and pricing."
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="animate-heartbeat-slow inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white font-bold text-lg px-10 py-4 rounded-full transition-colors duration-300 shadow-lg shadow-green-500/30"
            data-testid="hvac-audit-consult-btn"
          >
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Consult Now
          </a>
        </div>
      </div>
    </section>
  );
};

export default HvacAuditSection;
